import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useTimeMachine from '../../hooks/useTimeMachine';
import { Box, Button, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

export default function TimeMachineView() {
  const { getEffectiveTime, setOffsetMinutes, resetOffset } = useTimeMachine();
  const [info, setInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const offsetInputRef = useRef(null);
  const refreshRef = useRef(null);
  const desiredDateTimeRef = useRef(dayjs());

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getEffectiveTime();
      setInfo(data);
      if (
        offsetInputRef.current &&
        typeof data?.offsetMinutes === 'number' &&
        document.activeElement !== offsetInputRef.current
      ) {
        offsetInputRef.current.value = String(data.offsetMinutes);
      }
    } finally {
      setIsLoading(false);
    }
  }, [getEffectiveTime]);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    refreshRef.current?.();
  }, []);

  const currentOffset = useMemo(() => {
    const v = offsetInputRef.current?.value;
    const n = typeof v === 'string' ? parseInt(v, 10) : 0;
    return Number.isFinite(n) ? n : 0;
  }, [info]);

  const handleApply = useCallback(async () => {
    const raw = offsetInputRef.current?.value ?? '0';
    const offsetMinutes = parseInt(String(raw), 10);
    if (!Number.isFinite(offsetMinutes)) return;

    setIsApplying(true);
    try {
      const data = await setOffsetMinutes(offsetMinutes);
      setInfo(data);
    } finally {
      setIsApplying(false);
    }
  }, [setOffsetMinutes]);

  const applySafely = useCallback(() => {
    handleApply().catch(() => {});
  }, [handleApply]);

  const handleReset = useCallback(async () => {
    setIsApplying(true);
    try {
      const data = await resetOffset();
      setInfo(data);
      if (offsetInputRef.current) {
        offsetInputRef.current.value = '0';
      }
    } finally {
      setIsApplying(false);
    }
  }, [resetOffset]);

  const resetSafely = useCallback(() => {
    handleReset().catch(() => {});
  }, [handleReset]);

  const refreshSafely = useCallback(() => {
    refresh().catch(() => {});
  }, [refresh]);

  const handleOffsetKeyDown = useCallback(
    e => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      if (isApplying) return;
      applySafely();
    },
    [applySafely, isApplying]
  );

  const handleDesiredDateTimeChange = useCallback(v => {
    desiredDateTimeRef.current = v;
  }, []);

  const handleApplyFromDesiredDateTime = useCallback(async () => {
    const desired = desiredDateTimeRef.current;
    if (!desired || typeof desired.valueOf !== 'function') return;
    if (typeof desired.isValid === 'function' && !desired.isValid()) return;

    setIsApplying(true);
    try {
      const effectiveInfo = info?.serverNowISO ? info : await getEffectiveTime();
      if (!info?.serverNowISO) setInfo(effectiveInfo);

      const serverNowMs = Date.parse(String(effectiveInfo?.serverNowISO ?? ''));
      if (!Number.isFinite(serverNowMs)) return;

      const desiredMs = desired.valueOf();
      const offsetMinutes = Math.round((desiredMs - serverNowMs) / 60000);
      const data = await setOffsetMinutes(offsetMinutes);
      setInfo(data);
      if (offsetInputRef.current && document.activeElement !== offsetInputRef.current) {
        offsetInputRef.current.value = String(offsetMinutes);
      }
    } finally {
      setIsApplying(false);
    }
  }, [getEffectiveTime, info, setOffsetMinutes]);

  const applyFromDesiredDateTimeSafely = useCallback(() => {
    handleApplyFromDesiredDateTime().catch(() => {});
  }, [handleApplyFromDesiredDateTime]);

  const handleDesiredKeyDown = useCallback(
    e => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      if (isApplying) return;
      applyFromDesiredDateTimeSafely();
    },
    [applyFromDesiredDateTimeSafely, isApplying]
  );

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 2, maxWidth: 900, width: '100%', mx: 'auto' }}>
      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant='h5'>Time Machine</Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              id='offsetMinutes'
              label='Zeitverschiebung (Minuten)'
              type='number'
              size='small'
              defaultValue='0'
              inputRef={offsetInputRef}
              onKeyDown={handleOffsetKeyDown}
              inputProps={{ min: -43200, max: 43200, step: 1 }}
              sx={{
                width: { xs: '100%', sm: 260 },
                '& .MuiInputBase-root': { height: 40 },
              }}
            />

            <Button
              variant='contained'
              startIcon={<CheckIcon />}
              onClick={applySafely}
              disabled={isApplying}
              fullWidth
              sx={{ height: 40, flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
            >
              Anwenden
            </Button>
            <Button
              variant='outlined'
              color='warning'
              startIcon={<RestartAltIcon />}
              onClick={resetSafely}
              disabled={isApplying}
              fullWidth
              sx={{ height: 40, flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
            >
              Reset
            </Button>
            <Button
              variant='outlined'
              startIcon={<RefreshIcon />}
              onClick={refreshSafely}
              disabled={isLoading}
              fullWidth
              sx={{ height: 40, flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
            >
              Neu laden
            </Button>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <DateTimePicker
              label='Gewünschter Zeitpunkt (lokal)'
              defaultValue={desiredDateTimeRef.current}
              onChange={handleDesiredDateTimeChange}
              ampm={false}
              minutesStep={1}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  onKeyDown: handleDesiredKeyDown,
                  sx: { '& .MuiInputBase-root': { height: 40 } },
                },
              }}
            />
            <Button
              variant='contained'
              color='info'
              startIcon={<SyncAltIcon />}
              onClick={applyFromDesiredDateTimeSafely}
              disabled={isApplying}
              fullWidth
              sx={{ height: 40, flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
            >
              Offset übernehmen
            </Button>
          </Stack>

          <Divider />

          <Stack spacing={0.75}>
            <Typography variant='body2'>
              <strong>Aktiver Offset:</strong> {info?.offsetMinutes ?? currentOffset} min
            </Typography>

            <Typography variant='body2' sx={{ mt: 1 }}>
              <strong>Server Now (ISO/UTC):</strong> {info?.serverNowISO ?? '-'}
            </Typography>
            <Typography variant='body2'>
              <strong>Server Now (Local):</strong> {info?.serverNowLocal ?? '-'}
            </Typography>

            <Typography variant='body2' sx={{ mt: 1 }}>
              <strong>Berlin Now (Local):</strong> {info?.berlinNowLocal ?? '-'}
            </Typography>
            <Typography variant='body2'>
              <strong>Berlin Now (ISO/UTC):</strong> {info?.berlinNowISO ?? '-'}
            </Typography>

            <Typography variant='body2' sx={{ mt: 1 }}>
              <strong>Effective Now (ISO/UTC):</strong> {info?.effectiveNowISO ?? '-'}
            </Typography>
            <Typography variant='body2'>
              <strong>Effective Berlin Now (Local):</strong> {info?.effectiveBerlinNowLocal ?? '-'}
            </Typography>
            <Typography variant='body2'>
              <strong>Effective Berlin Now (ISO/UTC):</strong> {info?.effectiveBerlinNowISO ?? '-'}
            </Typography>

            <Typography variant='body2' sx={{ mt: 1 }}>
              <strong>Resolved TZ:</strong> {info?.resolvedTimeZone ?? '-'}
            </Typography>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
